import React, { createContext, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isValidUrl } from "../../utils/validation";
import { message } from "antd";
import { BannerMobileService } from "../../services/bannerMobileService";
import { BannerMobileDetail, BannerMobileFormData, BannerMobileListItem, BannerMobileOrderItem, defaultBannerMobileFormData, OperationStatus } from "./constants";

interface BannerMobileContextState {
	// Core Domain Data
	bannerMobileList: BannerMobileListItem[];
	bannerMobileDetail: BannerMobileDetail | null;
	bannerMobileForm: BannerMobileFormData;
	operationStatus: OperationStatus;

	// Setters
	setBannerMobileForm: React.Dispatch<React.SetStateAction<BannerMobileFormData>>;
	clearBannerMobileFormData: () => void;

	// Actions
	addBannerMobile: () => Promise<void>;
	getBannerMobileList: () => Promise<void>;
	updateBannerMobile: () => Promise<void>;
	removeBannerMobile: (id: string) => Promise<void>;
	getBannerMobileDetails: (id: string) => Promise<void>;
	updateBannerMobileOrders: (orderedList: BannerMobileOrderItem[]) => Promise<void>;
}

interface BannerMobileProviderProps {
    children: React.ReactNode;
}

const BannerMobileContext = createContext<BannerMobileContextState | undefined>(
	undefined
);

export const useBannerMobileContext = () => {
	const context = useContext(BannerMobileContext);
	if (!context) {
		throw new Error(
			"useBannerMobileContext must be used within a BannerMobileContextProvider"
		);
	}
	return context;
};

export const BannerMobileContextProvider: React.FC<BannerMobileProviderProps> = ({
	children,
}) => {
	const navigate = useNavigate();
	const [bannerMobileList, setBannerMobileList] = useState<BannerMobileListItem[]>([]);
	const [bannerMobileDetail, setBannerMobileDetail] = useState<BannerMobileDetail | null>(null);
	const [bannerMobileForm, setBannerMobileForm] = useState<BannerMobileFormData>(defaultBannerMobileFormData);

	const [operationStatus, setOperationStatus] = useState<OperationStatus>({ type: "idle" });

	const isBannerMobileDataValid = (data: BannerMobileFormData) => {
		if (!data.bannerMobileLocalizable || data.bannerMobileLocalizable.length === 0) {
			return false;
		}

		for (const localizable of data.bannerMobileLocalizable) {
			if (!localizable.firstHeader || !localizable.header || !localizable.culture) {
				return false;
			}
		}
		return true;
	};

	const clearBannerMobileFormData = () => {
		setBannerMobileForm(defaultBannerMobileFormData);
		setBannerMobileDetail(null);
	};

	const addBannerMobile = async () => {
		setOperationStatus({ type: "loading", operation: "add" });

		if (!isBannerMobileDataValid(bannerMobileForm) || !bannerMobileForm.image) {
			message.info("Lütfen tüm alanları doldurun.");
			setOperationStatus({ type: "idle" });
			return;
		}

		for (const localizable of bannerMobileForm.bannerMobileLocalizable) {
			if (localizable.url && !isValidUrl(localizable.url)) {
				message.info("Lütfen geçerli bir URL girin.");
				setOperationStatus({ type: "idle" });
				return;
			}
		}

		try {
			const response = await BannerMobileService.addBannerMobile(bannerMobileForm);

			if (response.data?.result?.isSuccessful) {
				message.success("Mobil Banner başarıyla eklendi.");
				setBannerMobileForm(defaultBannerMobileFormData);
				navigate("/mobile-banners");
			} else {
				message.error(response.data.result?.error?.message || "Beklenmedik Bir Hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "Beklenmedik Bir Hata oluştu.";
			setOperationStatus({ type: "error", operation: "add", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	};


	const getBannerMobileDetails = async (id: string) => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await BannerMobileService.getBannerMobileDetails(id);

			if (response.isSuccessful) {
				setBannerMobileDetail(response.result);

				setBannerMobileForm(prevState => ({
					...prevState,
					bannerId: response.result.bannerId,
					bannerMobileLocalizable: response.result.bannerMobileLocalizable,
					isActive: response.result.isActive,
				}));
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "Beklenmedik Bir Hata oluştu.";
			setOperationStatus({ type: "error", operation: "fetch", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	};

	const updateBannerMobile = async () => {
		setOperationStatus({ type: "loading", operation: "update" });

		if (!isBannerMobileDataValid(bannerMobileForm) || !bannerMobileForm.bannerId) {
			message.info("Lütfen tüm alanları doldurun.");
			setOperationStatus({ type: "idle" });
			return;
		}

		const activated = bannerMobileForm.isActive !== bannerMobileDetail?.isActive;
		const localizableChanged = JSON.stringify(bannerMobileForm.bannerMobileLocalizable) !== JSON.stringify(bannerMobileDetail?.bannerMobileLocalizable);
		const imagesChanged = bannerMobileForm.image;

		if (!activated && !localizableChanged && !imagesChanged) {
			message.info("Değişiklik yapılmadı.");
			setOperationStatus({ type: "idle" });
			return;
		}

		for (const localizable of bannerMobileForm.bannerMobileLocalizable) {
			if (localizable.url && !isValidUrl(localizable.url)) {
				message.info("Lütfen geçerli bir URL girin.");
				setOperationStatus({ type: "idle" });
				return;
			}
		}

		try {
			const response = await BannerMobileService.updateBannerMobile(bannerMobileForm);

			if (response.data.isSuccessful) {
				message.success("Mobil Banner başarıyla güncellendi.");
				setBannerMobileForm(defaultBannerMobileFormData);
				setBannerMobileDetail(null);
				navigate("/mobile-banners");
			} else {
				message.error(response.data.error?.message || "Hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "Beklenmedik Bir Hata oluştu.";
			setOperationStatus({ type: "error", operation: "update", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	};

	const getBannerMobileList = async () => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await BannerMobileService.getBannerMobileList();

			if (response.isSuccessful) {
				setBannerMobileList(response.result?.items || []);
				clearBannerMobileFormData();
			} else {
				message.error(response.error ? response.error : "Mobil Banner listesi yüklenirken hata oluştu.");
				setBannerMobileList([]);
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "Banner listesi yüklenirken hata oluştu.";
			setOperationStatus({ type: "error", operation: "fetch", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	};

	const removeBannerMobile = async (id: string) => {
		setOperationStatus({ type: "loading", operation: "delete" });

		try {
			const response = await BannerMobileService.removeBannerMobile(id);
			if (response.data?.result?.isSuccessful) {
				message.success("Mobil Banner başarıyla silindi.");
				await getBannerMobileList();
			} else {
				message.error(response.data?.result?.error ? response.data.result.error.message : "Mobil Banner silinirken hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "Beklenmedik Bir Hata oluştu.";
			setOperationStatus({ type: "error", operation: "delete", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	};

	const updateBannerMobileOrders = async (orderedList: BannerMobileOrderItem[]) => {
		setOperationStatus({ type: "loading", operation: "update" });
		try {
			const response = await BannerMobileService.updateBannerMobileOrders(orderedList);
			if (response.data?.result?.isSuccessful) {
				message.success("Mobil Banner sıralaması güncellendi.");
				navigate("/mobile-banners");
			} else {
				message.error(response.data?.result?.error ? response.data.result.error.message : "Banner sıralamaları güncellenirken bir hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "Beklenmedik Bir Hata oluştu.";
			setOperationStatus({ type: "error", operation: "update", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	};

	return (
		<BannerMobileContext.Provider
			value={{
				bannerMobileForm,
				bannerMobileList,
				bannerMobileDetail,
				setBannerMobileForm,
				operationStatus,
				addBannerMobile,
				removeBannerMobile,
				updateBannerMobileOrders,
				getBannerMobileList,
				getBannerMobileDetails,
				clearBannerMobileFormData,
				updateBannerMobile,
			}}
		>
			{children}
		</BannerMobileContext.Provider>
	);
};
